import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Home, Calendar, Clock } from 'lucide-react';
import { cleanPhone } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisitorSignIn = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    working_with_agent: '',
    agent_name: '',
    buying_timeline: '',
    budget_range: '',
    preferred_locations: '',
    interested_in_property: '',
    request_private_showing: false,
    notes: '',
    consent_communication: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/public/events/${eventId}`);
      setEvent(response.data);
      
      // Initialize form data based on template or default fields
      if (response.data.template) {
        const initialData = { consent_communication: false };
        response.data.template.fields.forEach(field => {
          initialData[field.field_name] = field.field_type === 'checkbox' ? false : '';
        });
        setFormData(initialData);
      }
    } catch (error) {
      toast.error('Event not found');
    }
  };

  const renderField = (field) => {
    const commonInputClass = "h-12 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4";
    
    switch (field.field_type) {
      case 'select':
        return (
          <select
            name={field.field_name}
            value={formData[field.field_name] || ''}
            onChange={handleChange}
            className={`${commonInputClass} w-full`}
            required={field.required}
          >
            <option value="">Select</option>
            {(field.options || []).map((option, idx) => (
              <option key={idx} value={option.toLowerCase().replace(/\s+/g, '-')}>{option}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
            <input
              type="checkbox"
              id={field.field_name}
              name={field.field_name}
              checked={formData[field.field_name] || false}
              onChange={handleChange}
              className="mt-1 h-5 w-5 text-[#1A4D2E] border-stone-300 rounded focus:ring-[#1A4D2E]"
            />
            <label htmlFor={field.field_name} className="text-sm text-stone-700 cursor-pointer">
              {field.label}
            </label>
          </div>
        );
      
      case 'textarea':
        return (
          <Textarea
            name={field.field_name}
            value={formData[field.field_name] || ''}
            onChange={handleChange}
            className="min-h-20 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4 py-3"
            placeholder={field.placeholder}
            required={field.required}
          />
        );
      
      default:
        return (
          <Input
            type={field.field_type}
            name={field.field_name}
            value={formData[field.field_name] || ''}
            onChange={handleChange}
            className={commonInputClass}
            placeholder={field.placeholder}
            required={field.required}
          />
        );
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Clean phone number before submission
      const cleanedData = {
        ...formData,
        phone: cleanPhone(formData.phone)
      };

      await axios.post(`${API}/events/${eventId}/visitors`, cleanedData);
      setSubmitted(true);
      toast.success('Thank you for signing in!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4">
        {/* Background */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1725042893312-5ec0dea9e369?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(8px) brightness(0.7)'
          }}
        />
        
        {/* Success Message */}
        <div className="glass-card rounded-2xl p-8 md:p-12 w-full max-w-md z-10 text-center" data-testid="success-message">
          <CheckCircle2 className="mx-auto mb-6 text-green-600" size={64} strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Thank You!</h1>
          <p className="text-lg text-stone-600 leading-relaxed">
            We've received your information and look forward to connecting with you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-y-auto py-8">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1725042893312-5ec0dea9e369?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(8px) brightness(0.7)'
        }}
      />
      
      {/* Sign-in Form */}
      <div className="glass-card rounded-2xl p-6 md:p-10 w-full max-w-2xl z-10 my-8" data-testid="visitor-signin-form">
        <div className="text-center mb-6">
          <div className="inline-block p-4 rounded-full bg-[#1A4D2E]/10 mb-4">
            <Home className="text-[#1A4D2E]" size={32} strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3" style={{fontFamily: '"Playfair Display", serif'}}>Welcome!</h1>
          <h2 className="text-xl font-semibold text-stone-700 mb-2">{event.property_address}</h2>
          <div className="flex items-center justify-center gap-4 text-sm text-stone-600">
            <div className="flex items-center gap-1">
              <Calendar size={16} strokeWidth={1.5} />
              <span>{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={16} strokeWidth={1.5} />
              <span>{event.time}</span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {event.template ? (
            // Render custom template fields
            <>
              {event.template.fields.sort((a, b) => a.order - b.order).map((field, index) => (
                field.field_type === 'checkbox' ? (
                  <div key={field.field_id}>
                    {renderField(field)}
                  </div>
                ) : (
                  <div key={field.field_id}>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">
                      {field.label} {field.required && '*'}
                    </Label>
                    {renderField(field)}
                  </div>
                )
              ))}
            </>
          ) : (
            // Default form (existing fields)
            <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Full Name *</Label>
              <Input
                data-testid="visitor-name-input"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="h-12 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
                required
              />
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Email *</Label>
              <Input
                data-testid="visitor-email-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="h-12 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Phone Number</Label>
              <PhoneInput
                data-testid="visitor-phone-input"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="h-12 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
              />
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Working with an agent?</Label>
              <select
                data-testid="visitor-agent-select"
                name="working_with_agent"
                value={formData.working_with_agent}
                onChange={handleChange}
                className="h-12 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          
          {/* Conditional Agent Name Field */}
          {formData.working_with_agent === 'yes' && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Agent Name</Label>
              <Input
                data-testid="visitor-agent-name-input"
                type="text"
                name="agent_name"
                value={formData.agent_name || ''}
                onChange={handleChange}
                className="h-12 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
                placeholder="Enter your agent's name"
              />
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Buying Timeline</Label>
              <select
                data-testid="visitor-timeline-select"
                name="buying_timeline"
                value={formData.buying_timeline}
                onChange={handleChange}
                className="h-12 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
              >
                <option value="">Select</option>
                <option value="immediately">Immediately (0-3 months)</option>
                <option value="soon">Soon (3-6 months)</option>
                <option value="later">Later this year (6-12 months)</option>
                <option value="exploring">Just exploring</option>
              </select>
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Budget Range</Label>
              <select
                data-testid="visitor-budget-select"
                name="budget_range"
                value={formData.budget_range}
                onChange={handleChange}
                className="h-12 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
              >
                <option value="">Select</option>
                <option value="under-300k">Under $300k</option>
                <option value="300k-500k">$300k - $500k</option>
                <option value="500k-750k">$500k - $750k</option>
                <option value="750k-1m">$750k - $1M</option>
                <option value="1m-2m">$1M - $2M</option>
                <option value="over-2m">Over $2M</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Preferred Locations</Label>
              <Input
                data-testid="visitor-locations-input"
                type="text"
                name="preferred_locations"
                value={formData.preferred_locations}
                onChange={handleChange}
                className="h-12 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
                placeholder="e.g., Miami Beach, Coral Gables"
              />
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Interested in this property?</Label>
              <select
                data-testid="visitor-interest-select"
                name="interested_in_property"
                value={formData.interested_in_property}
                onChange={handleChange}
                className="h-12 w-full bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="maybe">Maybe</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
            <input
              data-testid="visitor-showing-checkbox"
              type="checkbox"
              id="request_private_showing"
              name="request_private_showing"
              checked={formData.request_private_showing}
              onChange={handleChange}
              className="mt-1 h-5 w-5 text-[#1A4D2E] border-stone-300 rounded focus:ring-[#1A4D2E]"
            />
            <label htmlFor="request_private_showing" className="text-sm text-stone-700 cursor-pointer">
              I would like to request a private showing
            </label>
          </div>
          
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5 block">Notes or Questions</Label>
            <Textarea
              data-testid="visitor-notes-input"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="min-h-20 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4 py-3"
              placeholder="Any questions about the property?"
            />
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-[#1A4D2E]/5 rounded-lg border border-[#1A4D2E]/20">
            <input
              data-testid="visitor-consent-checkbox"
              type="checkbox"
              id="consent_communication"
              name="consent_communication"
              checked={formData.consent_communication}
              onChange={handleChange}
              className="mt-1 h-5 w-5 text-[#1A4D2E] border-stone-300 rounded focus:ring-[#1A4D2E]"
            />
            <label htmlFor="consent_communication" className="text-sm text-stone-700 cursor-pointer">
              I consent to receive communication about this property and similar listings via email, phone, or text.
            </label>
          </div>
          </>
          )}
          
          <Button
            data-testid="visitor-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A4D2E] text-white rounded-full h-14 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20 mt-6"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default VisitorSignIn;
